import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Integumentary subsection adapter
 * Handles skin assessment, wounds, and nail condition (6 fields)
 */
export class IntegumentaryAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Integumentary system assessment fields
    const skinColor = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR
    )?.at(0)

    const skinIssues = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES
    )

    const nails = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS
    )?.at(0)

    const identifiedWounds = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS
    )?.at(0)

    const woundNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.INTEGUMENTARY_SKIN_COLOR]: skinColor,
      [RocFormField.INTEGUMENTARY_SKIN_ISSUES]: skinIssues,
      [RocFormField.INTEGUMENTARY_NAILS]: nails,
      [RocFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS]: identifiedWounds,
      [RocFormField.INTEGUMENTARY_WOUND_NOTES]: woundNotes,
      [RocFormField.INTEGUMENTARY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.INTEGUMENTARY_SKIN_COLOR)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR,
        value: this.getFormDataField(RocFormField.INTEGUMENTARY_SKIN_COLOR)
      })
    }

    if (this.formDataFieldExists(RocFormField.INTEGUMENTARY_SKIN_ISSUES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES,
        value: this.getFormDataField(RocFormField.INTEGUMENTARY_SKIN_ISSUES)
      })
    }

    if (this.formDataFieldExists(RocFormField.INTEGUMENTARY_NAILS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS,
        value: this.getFormDataField(RocFormField.INTEGUMENTARY_NAILS)
      })
    }

    if (this.formDataFieldExists(RocFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS,
        value: this.getFormDataField(RocFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)
      })
    }

    if (this.formDataFieldExists(RocFormField.INTEGUMENTARY_WOUND_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES,
        value: this.getFormDataField(RocFormField.INTEGUMENTARY_WOUND_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.INTEGUMENTARY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.INTEGUMENTARY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
